import type { DataProfile } from './types';

/**
 * Build a STRUCTURAL profile from parsed rows. No raw cell values, no min/max/mean —
 * only counts, detected timestamp cadence/range, and per-column missing/zeros ratios.
 * `rows` is row-major (each row an array of string cells); `columns` are header names;
 * `timeColIndex` is the timestamp column used for resolution/range (default 0).
 */
export function computeDataProfile(
  rows: string[][],
  columns: string[],
  timeColIndex = 0,
): DataProfile {
  const rowCount = rows.length;
  const profile: DataProfile = { rowCount, columns: [...columns] };

  const times = rows
    .map((r) => Date.parse(r[timeColIndex] ?? ''))
    .filter((t) => !Number.isNaN(t));
  if (times.length >= 2) {
    const deltas: number[] = [];
    for (let i = 1; i < times.length; i++) deltas.push((times[i] - times[i - 1]) / 60000);
    const positive = deltas.filter((d) => d > 0).sort((a, b) => a - b);
    const median = positive.length ? positive[Math.floor(positive.length / 2)] : null;
    profile.timeColumn = {
      resolutionMinutes: median != null ? Math.round(median) : null,
      rangeStart: rows[0]?.[timeColIndex] ?? '',
      rangeEnd: rows[rowCount - 1]?.[timeColIndex] ?? '',
    };
  }

  const pct = (n: number) => (rowCount ? Math.round((n / rowCount) * 100) : 0);
  const quality: NonNullable<DataProfile['columnQuality']> = [];
  for (let c = 0; c < columns.length; c++) {
    if (c === timeColIndex) continue;
    let missing = 0;
    let zeros = 0;
    for (const r of rows) {
      const v = r[c];
      if (v == null || v.trim() === '') missing++;
      else if (Number(v) === 0) zeros++;
    }
    quality.push({ column: columns[c], missingPct: pct(missing), zerosPct: pct(zeros) });
  }
  if (quality.length) profile.columnQuality = quality;
  return profile;
}
